import { useCallback, useState } from "react";
import { useTreinamentosFilters } from "../hooks/Treinamentos_lista/filters"; //
import { useTreinamentosPagination } from "../hooks/Treinamentos_lista/pagination"; //
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { Treinamento } from "../../domain/models/treinamento";
import { TreinamentoForm } from "../schemas/Treinamentos_lista/treinamento";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { CopyTreinamentoForm } from "../schemas/Treinamentos_lista/copy_treinamento";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * useTreinamentosListaViewModel
 */

type LoadTreinamentosState = {
  tipoPesquisa?: number;
  valueField?: string;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useTreinamentosListaViewModel
 */

export const useTreinamentosListaViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    rh: {
      getAllTreinamentosUseCase,
      createTreinamentoUseCase,
      updateTreinamentoUseCase,
      deleteTreinamentoUseCase,
      copyTreinamentoUseCase,
      inativarTreinamentoUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();

  const [selectedTreinamento, setSelectedTreinamento] = useState<Treinamento>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [treinamentos, setTreinamentos] = useState<Treinamento[]>();

  const filters = useTreinamentosFilters();
  const pagination = useTreinamentosPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (error) => pushErrorNotification("Erro", [error]),
  });

  /**
   * Funções de carregamento
   */

  const loadTreinamentos = useCallback(
    async (funcState?: LoadTreinamentosState) => {
      try {
        const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredValueField = funcState?.valueField ?? filters.valueField;
        const filteredStatus = funcState?.status ?? filters.status;

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllTreinamentosUseCase.getAllTreinamentos(
          filteredTipoPesquisa,
          filteredValueField,
          filteredStatus,
          filteredPage,
          filteredRowsPerPage,
        );

        setTreinamentos(getAllTreinamentosUseCase.items);

        pagination.setRowsPerPage(getAllTreinamentosUseCase.pageSize);
        pagination.setTotalItems(getAllTreinamentosUseCase.totalItems);
      } catch (e: unknown) {
        setTreinamentos([]);
        throw e;
      }
    },
    [filters, pagination, getAllTreinamentosUseCase],
  );

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadTreinamentos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar treinamentos!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadTreinamentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [loadTreinamentos, pushErrorNotification]);

  const handleInativarSelecionados = useCallback(async () => {
    try {
      if (selectionModel && selectionModel.ids.size > 0) {
        setLoading(true);

        const treinamentos: number[] = [];
        for (var value of selectionModel.ids.values()) {
          if (typeof value === "number") {
            treinamentos.push(value as number);
          } else if (typeof value === "string") {
            treinamentos.push(Number(value));
          }
        }

        await inativarTreinamentoUseCase.inativarTreinamentos(treinamentos);

        await loadTreinamentos();
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [selectionModel, inativarTreinamentoUseCase, loadTreinamentos, pushErrorNotification]);

  const handleSubmitTreinamento = useCallback(
    async (form: TreinamentoForm) => {
      if (!loading && !saving) {
        try {
          setSaving(true);

          if (selectedTreinamento) {
            await updateTreinamentoUseCase.updateTreinamento(
              selectedTreinamento.idTreinamento,
              form.ativo,
              form.nome,
              form.versao,
              form.cargaHoraria,
              form.conteudoProgramatico,
            );
          } else {
            await createTreinamentoUseCase.createTreinamento(
              form.ativo,
              form.nome,
              form.versao,
              form.cargaHoraria,
              form.conteudoProgramatico,
            );
          }
          pushInfoNotification("Sucesso", "Treinamento salvo com sucesso!");

          await loadTreinamentos();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao criar treinamento!"));
        } finally {
          setSaving(false);
          setOpenCadastroModal(false);
          setSelectedTreinamento(undefined);
        }
      }
    },
    [
      loading,
      saving,
      selectedTreinamento,
      createTreinamentoUseCase,
      updateTreinamentoUseCase,
      loadTreinamentos,
      pushErrorNotification,
      pushInfoNotification,
    ],
  );

  const handleTipoPesquisaChange = useDebouncedCallback(
    debouncedRef,
    (tipoPesquisa: number) => {
      useTreinamentosFilters.getState().setTipoPesquisa(tipoPesquisa);
      return async () => {
        try {
          setLoading(true);
          useTreinamentosPagination.getState().setPage(1);
          await loadTreinamentos({ tipoPesquisa, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadTreinamentos, pushErrorNotification],
  );

  const handleValueFieldChange = useDebouncedCallback(
    debouncedRef,
    (valueField: string) => {
      useTreinamentosFilters.getState().setValueField(valueField);
      return async () => {
        try {
          setLoading(true);
          useTreinamentosPagination.getState().setPage(1);
          await loadTreinamentos({ valueField, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadTreinamentos, pushErrorNotification],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (status: number) => {
      useTreinamentosFilters.getState().setStatus(status);
      return async () => {
        try {
          setLoading(true);
          useTreinamentosPagination.getState().setPage(1);
          await loadTreinamentos({ status, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadTreinamentos, pushErrorNotification],
  );

  const handleTreinamentoRowSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
  }, []);

  const handleTreinamentoCadastroOpenState = useCallback(
    (idTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (idTreinamento && treinamentos) {
          setSelectedTreinamento(treinamentos.find((x) => x.idTreinamento === idTreinamento));
        } else {
          setSelectedTreinamento(undefined);
        }
      }
      setOpenCadastroModal(openState);
    },
    [treinamentos],
  );

  const handleTreinamentoDeleteOpenState = useCallback(
    (idTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (idTreinamento && treinamentos) {
          setSelectedTreinamento(treinamentos.find((x) => x.idTreinamento === idTreinamento));
        } else {
          setSelectedTreinamento(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [treinamentos],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useTreinamentosPagination.getState().setPage(page + 1);
        await loadTreinamentos({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
      } finally {
        setLoading(false);
      }
    },
    [loadTreinamentos, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useTreinamentosPagination.getState().setPage(1);
        useTreinamentosPagination.getState().setRowsPerPage(rowsPerPage);
        await loadTreinamentos({ rowsPerPage, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
      } finally {
        setLoading(false);
      }
    },
    [loadTreinamentos, pushErrorNotification],
  );

  const handleConfirmarDeletarTreinamento = useCallback(async () => {
    if (selectedTreinamento) {
      try {
        setSaving(true);
        await deleteTreinamentoUseCase.deleteTreinamento(selectedTreinamento.idTreinamento);
        await loadTreinamentos();
        pushInfoNotification("Sucesso", "Treinamento excluido com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao deletar treinamentos!"));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
        setSelectedTreinamento(undefined);
      }
    }
  }, [selectedTreinamento, deleteTreinamentoUseCase, loadTreinamentos, pushErrorNotification, pushInfoNotification]);

  const handleTreinamentoCopyOpenState = useCallback(
    (idTreinamento: number | null, openState: boolean) => {
      if (openState) {
        if (idTreinamento && treinamentos) {
          setSelectedTreinamento(treinamentos.find((x) => x.idTreinamento === idTreinamento));
        } else {
          setSelectedTreinamento(undefined);
        }
      }
      setOpenCopyModal(openState);
    },
    [treinamentos],
  );

  const handleTreinamentoCopyConfirm = useCallback(
    async (form: CopyTreinamentoForm) => {
      try {
        if (selectedTreinamento) {
          setSaving(true);

          await copyTreinamentoUseCase.copyTreinamento(selectedTreinamento.idTreinamento, form.nome, form.versao, form.cargaHoraria);

          await loadTreinamentos();
          pushInfoNotification("Sucesso", "Treinamento copiado com sucesso!");
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar treinamento!"));
      } finally {
        setSaving(false);
        setOpenCopyModal(false);
        setSelectedTreinamento(undefined);
      }
    },
    [selectedTreinamento, copyTreinamentoUseCase, loadTreinamentos, pushErrorNotification, pushInfoNotification],
  );

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedTreinamento) {
      changeHistory.handleOpenChangeHistory(selectedTreinamento.idTreinamento, LogTipoTabela.TREINAMENTO, false);
    }
  }, [selectedTreinamento, changeHistory]);

  return {
    loading,
    saving,
    selectionModel,
    selectedTreinamento,
    openCadastroModal,
    openDeleteModal,
    openCopyModal,
    errorMessage,
    treinamentos: treinamentos ?? [],
    filters,
    pagination,
    changeHistory,
    handleReload,
    handleInativarSelecionados,
    handleSubmitTreinamento,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handleStatusChange,
    handleTreinamentoRowSelectionChange,
    handleTreinamentoCadastroOpenState,
    handleTreinamentoDeleteOpenState,
    handlePageChange,
    handleRowsPerPageChange,
    handleConfirmarDeletarTreinamento,
    handleTreinamentoCopyOpenState,
    handleTreinamentoCopyConfirm,
    handleOpenChangeHistory,
  };
};
